import * as React from 'react';
import { Plus, Minus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Button } from '../ui/button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { DrawerContent, Drawer, DrawerTrigger } from '../ui/drawer';
import {
  updateField,
  updateReqData,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ClassPickerStateI {
  adult: number;
  infant: number;
  child: number;
  cabin: string;
  cabinId: string;
}

const ClassPicker = ({
  children,
  className,
  setData,
}: {
  children: React.ReactNode;
  className?: string;
  setData: (val: ClassPickerStateI) => void;
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stateData = useSelector((state: any) => state.flightSearch);

  const defaultPassengers = {
    adult: 1,
    child: 0,
    infant: 0,
    cabin: 'Economy',
    cabinId: 'EC',
  };

  const [selectedValues, setSelectedValues] = React.useState<ClassPickerStateI>(
    stateData.passengers || defaultPassengers,
  );

  React.useEffect(() => {
    if (stateData.passengers) {
      const validPassengers = {
        adult: stateData.passengers.adult || 1,
        child: stateData.passengers.child || 0,
        infant: stateData.passengers.infant || 0,
        cabin: stateData.passengers.cabin || 'Economy',
        cabinId: stateData.passengers.cabinId || 'EC',
      };
      setSelectedValues(validPassengers);
      setData(validPassengers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateData.passengers]);

  const handleRadioChange = (value: string) => {
    const cabinClass: Record<string, string> = {
      Economy: 'EC',
      Business: 'BS',
      'First Class': 'FC',
      'Premium Economy': 'PE',
    };

    const cabinId = cabinClass[value];
    const updatedValues = {
      ...selectedValues,
      cabin: value,
      cabinId,
    };

    setSelectedValues(updatedValues);
    setData(updatedValues);

    // Update Redux state
    const updatedStateData = {
      ...stateData,
      passengers: updatedValues,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(updateReqData(updatedStateData as any));
  };

  const onChange = (label: keyof ClassPickerStateI, value: number | string) => {
    setSelectedValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [label]: value,
      };
      setData(updatedValues);
      const updatedStateData = {
        ...stateData,
        passengers: updatedValues,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(updateReqData(updatedStateData as any));
      return updatedValues;
    });
  };

  const onSubmit = () => {
    const classData: Record<string, string> = {
      EC: 'Economy',
      BS: 'Business',
      FC: 'First Class',
      PE: 'Premium Economy',
    };

    const finalPassengers = {
      ...selectedValues,
      cabin: classData[selectedValues.cabinId] || 'Economy',
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(
      updateField({
        field: 'passengers',
        value: finalPassengers as any,
      }),
    );
    const updatedStateData = {
      ...stateData,
      passengers: finalPassengers,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(updateReqData(updatedStateData as any));
    setOpen(false);
  };

  // Clone children to pass open state
  const childrenWithProps = React.cloneElement(children as React.ReactElement, {
    isOpen: open,
  });

  if (isDesktop) {
    return (
      <div className="w-full max-w-sm md:col-span-3 lg:col-span-3">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild className={cn(`${className} w-full`)}>
            <div onClick={() => setOpen(true)}>{childrenWithProps}</div>
          </PopoverTrigger>
          <PopoverContent className="w-full bg-white">
            <ContentComponent
              onChange={onChange}
              value={selectedValues}
              onSubmit={onSubmit}
              handleRadioChange={handleRadioChange}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild className="w-full">
        <div onClick={() => setOpen(true)}>{childrenWithProps}</div>
      </DrawerTrigger>
      <DrawerContent className="items-center justify-center">
        <div className="mt-4">
          <ContentComponent
            onChange={onChange}
            value={selectedValues}
            onSubmit={onSubmit}
            handleRadioChange={handleRadioChange}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const ContentComponent = ({
  value,
  onChange,
  onSubmit,
  handleRadioChange,
}: {
  value: ClassPickerStateI;
  onChange: (label: keyof ClassPickerStateI, value: number | string) => void;
  onSubmit: () => void;
  handleRadioChange: (value: string) => void;
}) => {
  const handleClassChange = (label: string, value: string) => {
    handleRadioChange(value);
  };

  return (
    <div className="w-full">
      <div className="flex flex-row">
        <div className="basis-4/5">
          <h4 className="text-left text-base font-medium">Adult</h4>
          <h4 className="text-left text-xs text-text_secondary">
            12 Yrs & Above on the Day of Travel
          </h4>
        </div>
        <div className="basis-1/5">
          <Counter type="adult" value={value} onChange={onChange} />
        </div>
      </div>

      <div className="mt-4 flex flex-row">
        <div className="basis-4/5">
          <h4 className="text-left text-base font-medium">Children</h4>
          <h4 className="text-left text-xs text-text_secondary">
            2 - 12 Yrs on the Day of Travel
          </h4>
        </div>
        <div className="basis-1/5">
          <Counter type="child" value={value} onChange={onChange} />
        </div>
      </div>

      <div className="mt-4 flex flex-row">
        <div className="basis-4/5">
          <h4 className="text-left text-base font-medium">Infant</h4>
          <h4 className="text-left text-xs text-text_secondary">
            Under 2 Yrs on the Day of Travel
          </h4>
        </div>
        <div className="basis-1/5">
          <Counter type="infant" value={value} onChange={onChange} />
        </div>
      </div>

      <div className="mt-4 flex flex-col">
        <h4 className="text-left text-base font-medium">Select Travel Class</h4>
        <div className="mt-4">
          <RadioGroup defaultValue={value.cabinId}>
            <div className="flex flex-row">
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="EC"
                  id="r1"
                  onClick={() => handleClassChange('cabin', 'Economy')}
                />
                <Label htmlFor="r1">Economy</Label>
              </div>
              <div className="ml-9 flex items-center space-x-2">
                <RadioGroupItem
                  value="BS"
                  id="r2"
                  onClick={() => handleClassChange('cabin', 'Business')}
                />
                <Label htmlFor="r2">Business</Label>
              </div>
            </div>

            <div className="flex flex-row">
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="FC"
                  id="r3"
                  onClick={() => handleClassChange('cabin', 'First Class')}
                />
                <Label htmlFor="r3">First Class</Label>
              </div>
              <div className="ml-7 flex items-center space-x-2">
                <RadioGroupItem
                  value="PE"
                  id="r4"
                  onClick={() => handleClassChange('cabin', 'Premium Economy')}
                />
                <Label htmlFor="r4">Premium Economy</Label>
              </div>
            </div>
          </RadioGroup>
        </div>
      </div>
      <Button variant="default" className="mt-5 w-full" onClick={onSubmit}>
        <h4 className="text-sm font-medium text-white">Done</h4>
      </Button>
    </div>
  );
};

interface CounterProps {
  value: ClassPickerStateI;
  type: keyof ClassPickerStateI; // Ensuring the 'type' matches keys in 'value'
  onChange: (label: keyof ClassPickerStateI, newValue: number) => void;
}

const Counter = ({ value, type, onChange }: CounterProps) => {
  const { toast } = useToast();

  const onPress = (btnType: string) => {
    const currentValue = value[type] as number;
    const totalPassengers = value.adult + value.child;
    if (totalPassengers === 9) {
      toast({
        title: 'Error',
        description: 'Passenger count maximum limit reached.',
        variant: 'error',
      });
    }

    if (btnType === '-') {
      // Handle decrease
      if (
        (type !== 'adult' && currentValue > 0) ||
        (type === 'adult' && currentValue > 1 && currentValue > value.infant)
      ) {
        onChange(type, currentValue - 1);
      }
    } else if (btnType === '+') {
      // Handle increase
      if (type === 'infant') {
        // Infant count cannot exceed adult count
        if (currentValue < value.adult) {
          onChange(type, currentValue + 1);
        }
      } else if (type === 'adult' || type === 'child') {
        // Total passengers (adult + child) cannot exceed 9
        if (totalPassengers < 9) {
          onChange(type, currentValue + 1);
        }
      }
    }
  };

  return (
    <div className="flex flex-row items-center justify-center text-xs font-medium">
      <Minus
        onClick={() => onPress('-')}
        style={{ color: '#108DFF' }}
        className="m-1 w-4 cursor-pointer"
      />
      {value[type] || '0'}
      <Plus
        onClick={() => onPress('+')}
        style={{ color: '#108DFF' }}
        className="m-1 w-4 cursor-pointer"
      />
    </div>
  );
};

export default ClassPicker;
